import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { Option } from 'components/shared/Select';
import {
  PlanFilters,
  StrategicPlan,
} from 'services/strategic-plans/interfaces';
import { useAuth } from 'context/AuthContext';
import { Container, Content, FilterItem } from './styles';
import CultivationsSelect from './Selects/Cultivations';
import ChannelsSelect from './Selects/Channel';
import ProductsSelect from './Selects/Products';
import PlanAuthorSelect from './Selects/PlanAuthor';
import ApproverSelect from './Selects/Approver';
import ApproverProfileSelect from './Selects/ApproverProfile';
import Status from './Selects/Status';

interface FiltersProps {
  changeFilterHandler: (filters: PlanFilters) => void;
  hide?: boolean;
  hideStatusFilter?: boolean;
  hideChannelFilter?: boolean;
  tabPlans?: StrategicPlan[];
}

const Filters: React.FC<FiltersProps> = ({
  changeFilterHandler,
  hide,
  hideStatusFilter = false,
  hideChannelFilter = false,
  tabPlans,
}) => {
  const { participant } = useAuth();
  const isFirstRender = useRef(true);
  const location = useLocation();
  const [selectedProduct, setSelectedProduct] = useState<Option | null>(null);
  const [selectedApprover, setSelectedApprover] = useState<Option | null>(null);
  const [selectedAuthor, setSelectedAuthor] = useState<Option | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<Option | null>(null);
  const [selectedCrop, setSelectedCrop] = useState<Option | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<Option | null>(null);
  const [approverProfile, setApproverProfile] = useState<Option | null>(null);

  useEffect(() => {
    const checkFilters = localStorage.getItem('savingFilters');
    if (checkFilters === null) return;
    var filter = JSON.parse(checkFilters);

    setSelectedProduct(filter.selectedProduct);
    setSelectedApprover(filter.selectedApprover);
    setSelectedAuthor(filter.selectedAuthor);
    setSelectedStatus(filter.selectedStatus);
    setSelectedCrop(filter.selectedCrop);
    setSelectedChannel(filter.selectedChannel);
    setApproverProfile(filter.approverProfile);
  }, []);

  useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
    const checkFilters = localStorage.getItem('savingFilters');
    if (checkFilters === null) return;
    if (tabPlans === undefined) return;

    if (tabPlans.length === 0) {
      setSelectedProduct(null);
      setSelectedApprover(null);
      setSelectedAuthor(null);
      setSelectedStatus(null);
      setSelectedCrop(null);
      setSelectedChannel(null);
      setApproverProfile(null);
    }
    localStorage.removeItem('aprovando');
  }, [tabPlans]);

  // força o o reload da página quando a rota mudar
  useEffect(() => {
    // Lógica que deve ser executada sempre que a rota mudar
    // console.log('Rota mudou:', location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    let filters = {} as PlanFilters;

    if (selectedStatus) {
      filters = {
        ...filters,
        status: parseInt(selectedStatus.value, 10),
      };
    }

    if (selectedChannel) {
      filters = {
        ...filters,
        channelId: parseInt(selectedChannel.value, 10),
      };
    }

    if (selectedProduct) {
      filters = {
        ...filters,
        productId: parseInt(selectedProduct.value, 10),
      };
    }
    if (selectedCrop) {
      filters = {
        ...filters,
        cropId: parseInt(selectedCrop.value, 10),
      };
    }
    if (selectedApprover) {
      filters = {
        ...filters,
        approverId: parseInt(selectedApprover.value, 10),
      };
    }
    if (selectedAuthor) {
      filters = {
        ...filters,
        authorId: parseInt(selectedAuthor.value, 10),
      };
    }
    if (approverProfile) {
      filters = {
        ...filters,
        profileType: approverProfile.title,
      };
    }

    changeFilterHandler(filters);
  }, [
    changeFilterHandler,
    selectedApprover,
    selectedAuthor,
    selectedChannel,
    selectedCrop,
    selectedProduct,
    selectedStatus,
    approverProfile,
  ]);

  const actions =
    tabPlans && Object.values(tabPlans.flatMap(plan => plan.strategyActions));

  useEffect(() => {
    let filters = {
      selectedApprover,
      selectedAuthor,
      selectedChannel,
      selectedCrop,
      selectedProduct,
      selectedStatus,
      approverProfile,
    };

    let setFilters = JSON.stringify(filters);
    localStorage.setItem('savingFilters', setFilters);

  }, [
    selectedApprover,
    selectedAuthor,
    selectedChannel,
    selectedCrop,
    selectedProduct,
    selectedStatus,
    approverProfile,
  ]);

  return (
    <Container hide={hide}>
      <Content>
        {!hideStatusFilter && (
          <FilterItem>
            <Status value={selectedStatus} setValue={setSelectedStatus} />
          </FilterItem>
        )}
        {!hideChannelFilter && (
          <FilterItem>
            <ChannelsSelect
              value={selectedChannel}
              setValue={setSelectedChannel}
              plans={tabPlans || []}
            />
          </FilterItem>
        )}
        <FilterItem>
          <CultivationsSelect
            value={selectedCrop}
            setValue={setSelectedCrop}
            actions={actions || []}
          />
        </FilterItem>
        <FilterItem>
          <ProductsSelect
            value={selectedProduct}
            setValue={setSelectedProduct}
            actions={actions || []}
          />
        </FilterItem>
        <FilterItem>
          <PlanAuthorSelect
            value={selectedAuthor}
            setValue={setSelectedAuthor}
            plans={tabPlans || []}
          />
        </FilterItem>
        <FilterItem>
          <ApproverSelect
            value={selectedApprover}
            setValue={setSelectedApprover}
            plans={tabPlans || []}
          />
        </FilterItem>
        {participant.profile_value === 'CRM' && (
          <FilterItem>
            <ApproverProfileSelect
              value={approverProfile}
              setValue={setApproverProfile}
              plans={tabPlans || []}
            />
          </FilterItem>
        )}
      </Content>
    </Container>
  );
};

export default Filters;
